// ---------------------------------------------------------------------------
// DeviceFrame — sets the canvas content width based on the active breakpoint.
// Wraps the page content in a frame that visually resembles a device screen.
// ---------------------------------------------------------------------------

import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { BREAKPOINT_VIEWPORTS } from "@/features/page-editor/lib/responsive";
import type { SafeAreaInsets } from "@/features/preview/viewport/generated-app-viewport";
import { useEffect, useMemo, useState, type CSSProperties } from "react";

const RESERVED_STUDIO_HEIGHT_PX = 170;
const MIN_DEVICE_FRAME_HEIGHT_PX = 360;

interface DeviceFrameProps {
  children: React.ReactNode;
  sourceViewport?: {
    width: number;
    height: number;
  };
  /**
   * Exact scale selected by a containing preview surface.
   *
   * Omitted everywhere else so the editor keeps its existing height-fit
   * behavior. Preview supplies it because its available width changes when
   * the project navigator and Chat rail are open; a desktop frame must be
   * able to fit that real region instead of being clipped at 1280px.
   */
  renderScale?: number;
  /** Insets declared by the generated page/app viewport metadata. */
  safeAreaInsets?: SafeAreaInsets;
}

export function DeviceFrame({
  children,
  sourceViewport,
  renderScale,
  safeAreaInsets,
}: DeviceFrameProps) {
  // This width follows the creator and nothing else. A capture viewport used
  // to be allowed in here so the visual review could take a mobile shot, which
  // meant the app in front of the creator was re-laid-out — measured at up to
  // a minute per screenshot. Captures now happen on clones offscreen.
  const breakpoint = useEditorStore((s) => s.activeBreakpoint);
  // Callers pass sourceViewport only while generated geometry is the active
  // plane. A responsive user override omits it and therefore receives the
  // canonical breakpoint geometry below.
  const useSourceViewport = sourceViewport != null;
  const resolvedViewport = useSourceViewport
    ? sourceViewport
    : BREAKPOINT_VIEWPORTS[breakpoint];
  const { width, height } = resolvedViewport;
  const appliedSafeArea = useSourceViewport ? safeAreaInsets : undefined;
  const availableHeight = useAvailableDeviceFrameHeight();
  const frameScale = useMemo(() => {
    if (breakpoint === "desktop") return 1;
    return Math.min(1, availableHeight / height);
  }, [availableHeight, breakpoint, height]);
  const resolvedScale = renderScale ?? frameScale;
  const visualWidth = Math.round(width * resolvedScale);
  const visualHeight = Math.round(height * resolvedScale);

  return (
    <div
      className="flex flex-col items-stretch transition-[width] duration-300 ease-in-out"
      style={{ width: "100%", maxWidth: visualWidth }}
      data-testid="device-frame"
      data-breakpoint={breakpoint}
      data-source-viewport={useSourceViewport ? "true" : undefined}
      data-render-scale={Number(resolvedScale.toFixed(3))}
    >
      {/* Device screen — transform creates a containing block so position:fixed
          elements inside blocks are constrained to the device frame. */}
      <style>
        {`
          [data-cogo-device-frame-screen] .min-h-screen {
            height: var(--cogo-device-frame-height) !important;
            min-height: var(--cogo-device-frame-height) !important;
          }
          [data-cogo-device-frame-screen] .h-screen {
            height: var(--cogo-device-frame-height) !important;
          }
          [data-cogo-device-frame-screen] [data-cogo-studio-app-preview-boundary],
          [data-cogo-device-frame-screen] [data-cogo-studio-app-preview-boundary] * {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          [data-cogo-device-frame-screen] [data-cogo-studio-app-preview-boundary]::-webkit-scrollbar,
          [data-cogo-device-frame-screen] [data-cogo-studio-app-preview-boundary] *::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}
      </style>
      <div
        className="relative flex flex-col overflow-hidden rounded-lg border bg-background shadow-lg"
        style={
          {
            height: visualHeight,
            minHeight: visualHeight,
            "--cogo-device-frame-height": `${height}px`,
          } as CSSProperties
        }
        data-cogo-device-frame-screen="true"
      >
        <div
          className="min-h-0 shrink-0 box-border"
          style={{
            width,
            height,
            paddingTop: appliedSafeArea?.top,
            paddingRight: appliedSafeArea?.right,
            paddingBottom: appliedSafeArea?.bottom,
            paddingLeft: appliedSafeArea?.left,
            transform: `scale(${resolvedScale})`,
            transformOrigin: "top left",
          } as CSSProperties}
          data-safe-area={appliedSafeArea ? "declared" : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function useAvailableDeviceFrameHeight(): number {
  const [availableHeight, setAvailableHeight] = useState(() =>
    typeof window === "undefined"
      ? Number.POSITIVE_INFINITY
      : calculateAvailableDeviceFrameHeight(window.innerHeight),
  );

  useEffect(() => {
    const update = () => {
      setAvailableHeight(
        calculateAvailableDeviceFrameHeight(window.innerHeight),
      );
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return availableHeight;
}

function calculateAvailableDeviceFrameHeight(viewportHeight: number): number {
  return Math.max(
    MIN_DEVICE_FRAME_HEIGHT_PX,
    viewportHeight - RESERVED_STUDIO_HEIGHT_PX,
  );
}
